<script setup lang="ts">
import type { Entitlement, MonospacePolicyEntitlementResource, PolicyEntitlementAction } from '#layers/access/types/permissions/policy';
import { useOverlay } from '@nuxt/ui/composables';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePolicyEntitlementTranslations } from '#layers/access/composables/policy/use-policy-entitlement-translations';
import { usePolicyEntitlements } from '#layers/access/composables/policy/use-policy-entitlements';
import { showConfirmationDialog } from '#layers/ui/public';
import PolicyEntitlementDrawer from './PolicyEntitlementDrawer.vue';

interface PolicyEntitlementResourceActionProps {
	resource: MonospacePolicyEntitlementResource;
	action: PolicyEntitlementAction;
}

const props = defineProps<PolicyEntitlementResourceActionProps>();
const modelValue = defineModel<Entitlement[]>({ required: true });
const { t } = useI18n();

const { hasEntitlement, removeEntitlement, addEntitlementWildcard, selectedResourceIdsForEntitlement, hasWildcardEntitlement, setEntitlements } = usePolicyEntitlements({ entitlements: modelValue });

const computedHasEntitlement = computed({
	get() {
		return hasEntitlement(props.resource, props.action);
	},
	set(value) {
		if (value) {
			addEntitlementWildcard(props.resource, props.action);
		}
		else {
			if (hasWildcardEntitlement(props.resource, props.action) || !props.action.hasIdentifier) {
				removeEntitlement(props.resource, props.action);
			}
			else {
				confirmRemoveEntitlement();
			}
		}
	},
});

const hasResourceSelection = computed(() => props.action.hasIdentifier
	&& (hasWildcardEntitlement(props.resource, props.action) || selectedResourceIdsForEntitlement(props.resource, props.action).length > 0));

const buttonLabel = computed(() => {
	const count = selectedResourceIdsForEntitlement(props.resource, props.action).length;

	if (hasWildcardEntitlement(props.resource, props.action)) {
		return t('common.all_things', { things: t(`policy.entitlements.${props.resource.name}.title`, { count: 2 }) });
	}

	return `${count} ${t(`policy.entitlements.${props.resource.name}.title`, { count })}`;
});

const { entitlementName, entitlementDescription, resourceTitlePlural } = usePolicyEntitlementTranslations({
	resource: () => props.resource,
	action: () => props.action,
});

async function handleAddEntitlement() {
	if (!props.action.hasIdentifier || !props.resource.collectionId) {
		return;
	}

	const modal = useOverlay().create(PolicyEntitlementDrawer, {
		props: {
			collectionId: props.resource.collectionId,
			entitlements: modelValue.value,
			resource: props.resource,
			action: props.action,
		},
	});
	const instance = modal.open();
	const payload = await instance.result;

	if (payload?.data) {
		setEntitlements(payload.data.entitlements, props.resource, props.action);
	}
}

async function confirmRemoveEntitlement() {
	const count = selectedResourceIdsForEntitlement(props.resource, props.action).length;

	showConfirmationDialog({
		props: {
			title: t('policy.entitlements.confirm_remove_entitlement_title'),
			description: t('policy.entitlements.confirm_remove_entitlement_description', { count, resources: resourceTitlePlural.value }),
			confirm: { color: 'warning' },
		},
		onConfirm: () => {
			removeEntitlement(props.resource, props.action);
		},
	});
}
</script>

<template>
	<div class="flex items-center gap-2">
		<DSwitch
			:id="`${resource.name}.${action.name}`"
			v-model="computedHasEntitlement"
			size="sm"
			class="items-center"
			:label="entitlementName"
			:ui="{ label: 'text-sm' }"
		/>
		<div class="flex flex-1 items-center gap-2">
			<DTooltip v-if="entitlementDescription" :text="entitlementDescription">
				<DIcon name="lucide:info" class="text-dimmed" />
			</DTooltip>
			<DTooltip
				v-if="hasResourceSelection"
				:text="$t(`common.select_thing`, { thing: resourceTitlePlural })"
			>
				<DButton
					:data-testid="`entitlement-selection-${resource.name}-${action.name}`"
					size="xs"
					variant="soft"
					color="primary"
					:label="buttonLabel"
					@click="handleAddEntitlement"
				/>
			</DTooltip>
		</div>
	</div>
</template>
